import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { SxProps, Theme } from '@mui/material/styles';

import type { ILead } from 'src/services/api';
import type { IProperty } from 'src/services/api';
import { useAmiIncomeLimits } from 'src/services/api/ami/use-ami-income-limits';
import ExpandableItem from 'src/components/lead-sidebar/LeadSidebarExpandableItem';
import EditFactButton from 'src/components/lead-sidebar/EditFactButton';
import { FlexColumn } from 'src/components/flex';
import {
    resolveAmiBands,
    needsGlobalLimits,
    shouldShowAmiCard,
    type AmiBandStatus,
} from 'src/components/lead-sidebar/ami-bands';

import styles from 'src/components/lead-sidebar/AmiBandsCard.module.css';

type Props = {
    lead: ILead;
    property: IProperty;
};

const STATUS_SX: Record<AmiBandStatus, SxProps<Theme>> = {
    below: { bgcolor: 'success.50', borderColor: 'success.200', color: 'success.800' },
    near: { bgcolor: 'warning.50', borderColor: 'warning.200', color: 'warning.800' },
    over: { bgcolor: 'error.50', borderColor: 'error.200', color: 'error.800' },
    unknown: { bgcolor: 'grey.100' },
};

const LEGEND: { color: string; label: string }[] = [
    { color: 'success.main', label: 'Below limit' },
    { color: 'warning.main', label: 'Near limit' },
    { color: 'error.main', label: 'Over limit' },
];

const formatAnnual = (amount: number): string => `$${Math.round(amount).toLocaleString()}`;

function Unavailable({ message }: { message: string }) {
    return (
        <Typography variant="caption" color="text.secondary" className={styles.caption}>
            {message}
        </Typography>
    );
}

export default function AmiBandsCard({ lead, property }: Props) {
    const shouldShow = shouldShowAmiCard(property);
    const needsGlobal = needsGlobalLimits(property, lead);

    const { data: globalLimits, isLoading } = useAmiIncomeLimits(
        needsGlobal ? property.county_fips : undefined,
        lead.household_size
    );

    const rows = useMemo(
        () => resolveAmiBands({ property, lead, globalLimits }),
        [property, lead, globalLimits]
    );

    if (!shouldShow) {
        return null;
    }

    const hasIncome = lead.annual_income !== null && lead.annual_income !== undefined;
    const hasHousehold = lead.household_size !== null && lead.household_size !== undefined;
    const showLegend = rows.length > 0 && hasIncome && hasHousehold;

    const editableHouseholdSize = (label: React.ReactNode) => (
        <EditFactButton
            leadUuid={lead.uuid}
            title="Household Size"
            testId="ami-household_size"
            factKey="household_size"
            value={hasHousehold ? String(lead.household_size) : ''}
        >
            <Typography
                component="span"
                variant="caption"
                color="text.primary"
                className={styles.editableHousehold}
            >
                {label}
            </Typography>
        </EditFactButton>
    );

    let contextNode: React.ReactNode;
    if (!hasHousehold) {
        contextNode = <>{editableHouseholdSize('Add a household size')} to see income limits</>;
    } else if (!hasIncome) {
        contextNode = (
            <>Add income to check eligibility for household size {editableHouseholdSize(lead.household_size)}</>
        );
    } else {
        contextNode = <>For household size {editableHouseholdSize(lead.household_size)}</>;
    }

    let body: React.ReactNode;
    if (rows.length > 0) {
        body = (
            <FlexColumn>
                <Typography variant="caption" color="text.secondary" className={styles.caption}>
                    {contextNode}
                </Typography>
                <div className={styles.table}>
                    <div className={styles.headerRow}>
                        <Typography variant="caption" color="text.secondary">
                            Band
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Income limit
                        </Typography>
                    </div>
                    {rows.map((row) => (
                        <Box
                            key={row.bandLabel}
                            className={styles.row}
                            sx={STATUS_SX[row.status]}
                            data-testid={`ami-band-row-${row.bandPercent}`}
                        >
                            <Typography variant="subtitle2">
                                {row.bandLabel}
                            </Typography>
                            <Typography variant="body2" className={styles.amount}>
                                {row.amount === null ? '—' : `${formatAnnual(row.amount)}/yr`}
                            </Typography>
                        </Box>
                    ))}
                </div>
                {showLegend && (
                    <div className={styles.legend}>
                        {LEGEND.map(({ color, label }) => (
                            <span key={label} className={styles.legendItem}>
                                <Box
                                    component="span"
                                    className={styles.legendSwatch}
                                    sx={{ bgcolor: color }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    {label}
                                </Typography>
                            </span>
                        ))}
                    </div>
                )}
            </FlexColumn>
        );
    } else if (needsGlobal && !property.county_fips) {
        body = <Unavailable message="AMI income limits are still being looked up for this property." />;
    } else if (needsGlobal && isLoading) {
        body = <Unavailable message="Loading income limits…" />;
    } else {
        body = <Unavailable message="AMI income limits are unavailable for this property." />;
    }

    return (
        <div data-testid="ami-bands-card">
            <ExpandableItem
                title="AMI bands"
                tooltipTitle="Area Median Income limits for this affordable-housing property, compared to the lead's income."
            >
                {body}
            </ExpandableItem>
        </div>
    );
}
